import {
  HGEra,
  HGIntersectionType,
  HGInterval,
  HGIntervalAPI,
  HGIntervalSerialized,
  HGIntervalType,
  HGTimeRecordPlus,
} from "./types";
import { calculateIntersection } from "./intersection";

export function constructInterval(n: bigint, it: HGIntervalType): HGInterval {
  return {
    n,
    it,
  };
}

export function intervalsAreEqual(
  interval1: HGInterval | null | undefined,
  interval2: HGInterval | null | undefined,
): boolean {
  if (!interval1 || !interval2) return false;
  return interval1.n == interval2.n && interval1.it == interval2.it;
}

export function constructIntervalFromAPI(
  n: string,
  it: HGIntervalType,
  era: HGEra,
): HGInterval {
  return {
    n: BigInt(era == HGEra.BC ? "-" + n : n),
    it,
  };
}

export function constructIntervalForAPI(interval: HGInterval): HGIntervalAPI {
  return {
    n: interval.n < 0 ? (-interval.n).toString() : interval.n.toString(),
    it: interval.it,
    era: interval.n < 0 ? HGEra.BC : HGEra.AD,
  };
}

export function serializeInterval(interval: HGInterval): HGIntervalSerialized {
  return {
    n: interval.n.toString(),
    it: interval.it,
  };
}

export function deserializeInterval(
  interval: HGIntervalSerialized,
): HGInterval {
  return {
    n: BigInt(interval.n),
    it: interval.it,
  };
}

/**
 * Moves an interval number by `shift`, skipping the n that does not exist:
 * negative n is BC, positive is AD, and there is no n == 0. The single place
 * this rule is encoded — everything stepping or counting n goes through here.
 */
export function shiftN(n: bigint, shift: bigint): bigint {
  const shifted = n + shift;
  if (n > 0n && shifted <= 0n) return shifted - 1n;
  if (n < 0n && shifted >= 0n) return shifted + 1n;
  return shifted;
}

/** How many interval numbers exist from `from` to `to`, both ends included. */
export function countN(from: bigint, to: bigint): number {
  let count = to - from + 1n;
  if (from < 0n && to > 0n) count -= 1n; // n == 0 does not exist
  return Number(count);
}

export function shiftIntervalLeft(
  interval: HGInterval,
  shift: number,
): HGInterval {
  if (shift < 1) throw Error;
  return { n: shiftN(interval.n, -BigInt(shift)), it: interval.it };
}

export function shiftIntervalRight(
  interval: HGInterval,
  shift: number,
): HGInterval {
  if (shift < 1) throw Error;
  return { n: shiftN(interval.n, BigInt(shift)), it: interval.it };
}

export function shiftInterval(interval: HGInterval, shift: number): HGInterval {
  if (shift == 0) {
    return interval;
  }
  if (shift < 0) {
    return shiftIntervalLeft(interval, -shift);
  } else {
    return shiftIntervalRight(interval, shift);
  }
}

export function compareRecordsWithinInterval(range: [bigint, bigint]) {
  return (recordA: HGTimeRecordPlus, recordB: HGTimeRecordPlus) => {
    const intersectionTypeScores = {
      [HGIntersectionType.Exact]: 100,
      [HGIntersectionType.Contained]: 10,
      [HGIntersectionType.PartLeft]: 10,
      [HGIntersectionType.PartRight]: 10,
      [HGIntersectionType.Starts]: 10,
      [HGIntersectionType.Ends]: 10,
      [HGIntersectionType.Transit]: 1,
      [HGIntersectionType.None]: 0,
    };
    const intersectionA = calculateIntersection(range, [
      recordA.start_second,
      recordA.end_second,
    ]);
    const intersectionB = calculateIntersection(range, [
      recordB.start_second,
      recordB.end_second,
    ]);
    if (!intersectionA || !intersectionA.offsets) {
      return -1;
    } else if (!intersectionB || !intersectionB.offsets) {
      return 1;
    }

    const scoreA = intersectionTypeScores[intersectionA.intersectionType];
    const scoreB = intersectionTypeScores[intersectionB.intersectionType];

    if (scoreA == scoreB) {
      if (
        intersectionA.intersectionType == HGIntersectionType.Exact ||
        intersectionA.intersectionType == HGIntersectionType.Transit
      )
        return 0;

      if (intersectionA.offsets.begin == intersectionB.offsets.begin) {
        return intersectionA.offsets.end - intersectionB.offsets.end;
      } else {
        return intersectionA.offsets.begin - intersectionB.offsets.begin;
      }
    }
    return scoreB - scoreA;
  };
}
